import { useState } from 'react';
import type { ReactElement } from 'react';

import { useApp } from '@weke/ui';
import { formatMoney, formatPercentBp } from '@weke/ui';
import { ActivityPanel } from './ActivityPanel.js';
import { AccountPanel } from './AccountPanel.js';
import { HistoryPanel } from './HistoryPanel.js';
import { PositionsPanel } from './PositionsPanel.js';

type Tab = 'positions' | 'orders' | 'history' | 'account' | 'alerts' | 'journal';

/**
 * The toolbox: the account's own book, in one docked strip.
 *
 * The summary line repeats balance, equity and free margin above the grid because
 * that is the number a trader checks while looking at a position, and making them
 * look up at a header for it is how a screen gets two sources of truth.
 *
 * The panels inside keep their own logic and test ids; the CSS inside
 * `.toolbox-body` drops their duplicated frames so this reads as one region.
 */
export function Toolbox(): ReactElement {
  const app = useApp();
  const [tab, setTab] = useState<Tab>('positions');
  const account = app.account;
  const digits = account?.digits ?? 2;
  const openPositions = Object.keys(app.book.positions).length;
  const working = Object.keys(app.book.orders).length;
  const alerts = app.alerts.length;

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'positions', label: 'Positions', count: openPositions },
    { id: 'orders', label: 'Orders', count: working },
    { id: 'history', label: 'History' },
    { id: 'account', label: 'Account' },
    { id: 'alerts', label: 'Alerts', count: alerts },
    { id: 'journal', label: 'Journal' },
  ];

  return (
    <section className="panel min-h-0 flex-1" aria-label="Toolbox">
      <header className="panel-head">
        <span>Toolbox</span>
        <span className="ml-auto flex items-center gap-4 pr-1 normal-case tracking-normal">
          <span className="text-2xs text-muted">
            Balance{' '}
            <span className="num text-text">
              {account === null ? '—' : formatMoney(account.balance_minor, digits)}
            </span>{' '}
            {account?.currency ?? ''}
          </span>
          <span className="text-2xs text-muted">
            Equity{' '}
            <span className="num text-text">
              {account === null ? '—' : formatMoney(account.equity_minor, digits)}
            </span>
          </span>
          <span className="text-2xs text-muted">
            Free margin{' '}
            <span className="num text-text">
              {account === null ? '—' : formatMoney(account.free_margin_minor, digits)}
            </span>
          </span>
          <span className="text-2xs text-muted">
            Level{' '}
            <span
              className={`num ${
                account?.margin_call === true ? 'sell' : 'text-text'
              }`}
            >
              {account === null ? '—' : formatPercentBp(account.margin_level_bp)}
            </span>
          </span>
        </span>
      </header>
      <div className="toolbox-body panel-body" data-testid="toolbox">
        {tab === 'positions' ? <PositionsPanel initialTab="positions" hideTabs /> : null}
        {tab === 'orders' ? <PositionsPanel initialTab="orders" hideTabs /> : null}
        {tab === 'history' ? <HistoryPanel /> : null}
        {tab === 'account' ? <AccountPanel /> : null}
        {tab === 'alerts' ? <ActivityPanel mode="alerts" /> : null}
        {tab === 'journal' ? <ActivityPanel mode="journal" /> : null}
      </div>
      <div className="tabs" role="tablist" aria-label="Toolbox tabs">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab === entry.id}
            data-testid={`toolbox-${entry.id}`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
            {entry.count === undefined || entry.count === 0 ? null : (
              <span className="faint">{entry.count}</span>
            )}
          </button>
        ))}
      </div>
    </section>
  );
}
